import {
  type RepoConfig,
  TimeoutError,
} from '@/app/sync/github';
import type {
  OnProgress,
  Progress,
  SyncResult,
} from '@/app/sync/sync';
import {
  type Database,
  makeDatabase,
} from './database';
import type { Job } from './worker';

type Reply = {
  id: number,
  result?: unknown,
  error?: string,
  timedOut?: boolean,
  // ahead of the answer, which follows under the same id
  progress?: Progress,
};

const worker = new Worker(new URL('./worker.ts', import.meta.url), {
  type: 'module',
});

const pending = new Map<
  number,
  {
    resolve: (v: unknown) => void,
    reject: (e: Error) => void,
    onProgress?: OnProgress,
  }
>();
let nextId = 0;

worker.onmessage = (
  { data: { id, result, error, timedOut, progress } }: MessageEvent<Reply>,
) => {
  const p = pending.get(id);
  if (progress) return p?.onProgress?.(progress);
  pending.delete(id);
  if (error !== undefined) {
    p?.reject(timedOut ? new TimeoutError(error) : new Error(error));
  } else p?.resolve(result);
};

const call = (job: Job, onProgress?: OnProgress) =>
  new Promise<unknown>((resolve, reject) => {
    const id = nextId++;
    pending.set(id, { resolve, reject, onProgress });
    worker.postMessage({ id, job });
  });

export const db: Database = makeDatabase(call);

// runs a sync in the worker, next to the database; the key crosses over as
// the non-extractable CryptoKey it is
export const syncInWorker = (
  config: RepoConfig,
  key: CryptoKey,
  onProgress: OnProgress,
) => call({ kind: 'sync', config, key }, onProgress) as Promise<SyncResult>;

// stages every unpushed change for the service worker; how many there were
export const stageInWorker = (key: CryptoKey) =>
  call({ kind: 'stage', key }) as Promise<number>;
